/**
 * AppSwitcher — plattformweiter App-Wechsler (9-Punkte-Grid in der Kopfleiste).
 *
 * REFERENZ-IMPLEMENTIERUNG laut UX-Konzept (RMNC-Leitstand/docs/UX_KONZEPT.md §1).
 * Zur Übernahme in andere Apps: Datei 1:1 kopieren, beim Einbau nur die Props
 * `currentAppKey` und `roles` setzen. Keine weiteren Abhängigkeiten (kein Radix,
 * kein React-Query) — bewusst selbst-enthalten.
 *
 * Datenquelle: /links.json (Laufzeit-Fetch, gleiches Schema wie das Portal
 * plus Schlüssel "portal"). Stage/Prod-Umschaltung = Datei auf dem Server
 * anpassen, kein Rebuild. Fällt der Fetch aus, bleibt der Heimweg zum Portal
 * über den hart kodierten Fallback erhalten.
 *
 * Icons + Akzentfarben sind exakt die Portal-Kacheln (rmnc-portal/src/apps.tsx).
 */
import { useEffect, useRef, useState, type ReactNode } from "react"

// ---------------------------------------------------------------------------
// Plattform-Apps (Icons/Farben/Untertitel = Portal-Kacheln, nicht verändern)
// ---------------------------------------------------------------------------

/**
 * Rueckweg zum Portal, wenn links.json nicht geladen werden kann. Im
 * Pfad-Betrieb liegt das Portal garantiert am Ursprung; auf der Altadresse
 * waere "/" die App selbst (rmnc-plattform Entscheidung 010).
 */
const PORTAL_FALLBACK_URL =
  import.meta.env.BASE_URL !== "/" ? "/" : "https://portal.rmnc.app"

const svgProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  width: 24,
  height: 24,
  "aria-hidden": true,
} as const

const hasPrefix = (roles: string[], prefix: string) =>
  roles.some((r) => r.startsWith(prefix))

/**
 * Beide Rollen-Konventionen zählen: flach `app:<name>` und `app:<name>:<rolle>`.
 * Plattform-Admins (`admin`) sehen grundsätzlich alle Apps.
 */
const hasAppRole = (roles: string[], app: string) =>
  roles.includes("admin") || roles.includes(`app:${app}`) || hasPrefix(roles, `app:${app}:`)

interface PlatformApp {
  key: string
  title: string
  subtitle: string
  accent: string
  icon: ReactNode
  hasAccess: (roles: string[]) => boolean
}

export const PLATFORM_APPS: PlatformApp[] = [
  {
    key: "leitstand",
    title: "Leitstand",
    subtitle: "Produktionssteuerung & PPS",
    accent: "#4da3ff",
    icon: (
      <svg {...svgProps}>
        <path d="M3 21V11l5 3v-3l5 3v-3l8 4.8V21z" />
        <path d="M6.5 11V4.5h3V11" />
        <path d="M13 21v-3h3v3" />
      </svg>
    ),
    hasAccess: (roles) =>
      roles.includes("admin") ||
      hasPrefix(roles, "produktion_") ||
      hasPrefix(roles, "leitstand:") ||
      hasAppRole(roles, "leitstand"),
  },
  {
    key: "simulation",
    title: "Simulation",
    subtitle: "Produktionsplanung im Zeitraffer",
    accent: "#8e44ad",
    icon: (
      <svg {...svgProps}>
        <circle cx="12" cy="12" r="9" />
        <path d="M10 8.5 15.5 12 10 15.5z" />
        <path d="M12 3v2M21 12h-2M12 21v-2M3 12h2" />
      </svg>
    ),
    hasAccess: (roles) =>
      roles.includes("admin") ||
      roles.includes("produktion_leitung") ||
      hasPrefix(roles, "app:leitstand:leitung"),
  },
  {
    key: "leitstand-plus",
    title: "Leitstand-Plus",
    subtitle: "Produktionsplanung & Prozesssteuerung",
    accent: "#1d4ed8",
    icon: (
      // Fabrik mit Plus (Nachfolger-Anklang an die Leitstand-Kachel)
      <svg {...svgProps}>
        <path d="M3 21V11l5 3v-3l5 3v-3l8 4.8V21z" />
        <path d="M6.5 11V4.5h3V11" />
        <path d="M17.5 4.5v5M15 7h5" />
      </svg>
    ),
    // Schattenbetrieb: vorerst nur Admins und Produktionsleitung
    hasAccess: (roles) =>
      roles.includes("admin") ||
      roles.includes("leitstand:admin") ||
      roles.includes("leitstand:produktion_leitung") ||
      roles.includes("produktion_leitung") ||
      hasPrefix(roles, "app:leitstand-plus"),
  },
  {
    key: "personal",
    title: "Personal",
    subtitle: "Mitarbeiter & Schichtkalender",
    accent: "#2ecc71",
    icon: (
      <svg {...svgProps}>
        <circle cx="9" cy="8" r="3.2" />
        <path d="M3.5 20c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
        <circle cx="17" cy="9.5" r="2.4" />
        <path d="M16 15.2c2.7.2 4.5 2 4.5 4.8" />
      </svg>
    ),
    hasAccess: (roles) => hasAppRole(roles, "personal"),
  },
  {
    key: "technik",
    title: "Technik",
    subtitle: "Anlagen & Instandhaltung",
    accent: "#e67e22",
    icon: (
      <svg {...svgProps}>
        <path d="M14.7 6.3a4 4 0 0 0-5.4 5.1L4 16.7a2 2 0 1 0 2.8 2.8l5.3-5.3a4 4 0 0 0 5.1-5.4l-2.6 2.6-2.3-.7-.7-2.3z" />
        <circle cx="18" cy="18" r="2.2" />
      </svg>
    ),
    hasAccess: (roles) => hasAppRole(roles, "technik"),
  },
  {
    key: "stammdaten",
    title: "Stammdaten",
    subtitle: "Organisationen, Artikel & Rohstoffe",
    accent: "#9b59b6",
    icon: (
      <svg {...svgProps}>
        <ellipse cx="12" cy="5.5" rx="7" ry="2.8" />
        <path d="M5 5.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6" />
        <path d="M5 11.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6" />
      </svg>
    ),
    hasAccess: (roles) => hasAppRole(roles, "stammdaten"),
  },
  {
    key: "energie",
    title: "Energie",
    subtitle: "Verbräuche & SEU-Monitoring",
    accent: "#f1c40f",
    icon: (
      <svg {...svgProps}>
        <path d="M13 2 5 13.5h5.5L10 22l8-11.5h-5.5z" />
      </svg>
    ),
    hasAccess: (roles) => hasAppRole(roles, "energie"),
  },
  {
    key: "dokubase",
    title: "Dokubase",
    subtitle: "Handbuch & Dokumentation",
    accent: "#1abc9c",
    icon: (
      <svg {...svgProps}>
        <path d="M12 6.5C10.5 5 8 4.5 5.5 4.5c-.8 0-1.6.05-2.5.2V18c.9-.15 1.7-.2 2.5-.2 2.5 0 5 .5 6.5 2 1.5-1.5 4-2 6.5-2 .8 0 1.6.05 2.5.2V4.7c-.9-.15-1.7-.2-2.5-.2-2.5 0-5 .5-6.5 2z" />
        <path d="M12 6.5v13.3" />
      </svg>
    ),
    hasAccess: () => true,
  },
]

const PORTAL_ENTRY = {
  key: "portal",
  title: "Portal",
  subtitle: "Alle Anwendungen",
  accent: "#4da3ff",
  icon: (
    <svg {...svgProps}>
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5.5 10v9.5h13V10" />
      <path d="M10 19.5v-5h4v5" />
    </svg>
  ),
} as const

// ---------------------------------------------------------------------------
// Links (Laufzeit-Konfiguration)
// ---------------------------------------------------------------------------

type Links = Record<string, string>

let linksCache: Links | null = null

export function useLinks(): Links | null {
  const [links, setLinks] = useState<Links | null>(linksCache)
  useEffect(() => {
    if (linksCache) return
    let cancelled = false
    fetch("/links.json", { cache: "no-cache" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data: Links) => {
        linksCache = data
        if (!cancelled) setLinks(data)
      })
      .catch(() => {
        // Fallback: der Heimweg zum Portal darf nie ausfallen.
        const fallback = { portal: PORTAL_FALLBACK_URL }
        linksCache = fallback
        if (!cancelled) setLinks(fallback)
      })
    return () => {
      cancelled = true
    }
  }, [])
  return links
}

// ---------------------------------------------------------------------------
// Komponente
// ---------------------------------------------------------------------------

export interface AppSwitcherProps {
  /** Schlüssel der aktuellen App (z. B. "stammdaten") — wird als „Sie sind hier" markiert. */
  currentAppKey: string
  /** Rollen des angemeldeten Users (Claim `roles`); filtert die sichtbaren Apps. */
  roles: string[]
}

export function AppSwitcher({ currentAppKey, roles }: AppSwitcherProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const links = useLinks()

  // Schließen bei Klick außerhalb
  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("pointerdown", onPointerDown)
    return () => document.removeEventListener("pointerdown", onPointerDown)
  }, [open])

  // Fokus beim Öffnen auf den ersten Eintrag
  useEffect(() => {
    if (open) {
      panelRef.current
        ?.querySelector<HTMLElement>("a[href], button:not([disabled])")
        ?.focus()
    }
  }, [open])

  // Esc schließt (Fokus zurück auf den Button), Tab zirkuliert (Focus-Trap)
  const onPanelKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation()
      setOpen(false)
      buttonRef.current?.focus()
      return
    }
    if (e.key !== "Tab" || !panelRef.current) return
    const focusables = Array.from(
      panelRef.current.querySelectorAll<HTMLElement>("a[href], button:not([disabled])"),
    )
    if (focusables.length === 0) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  const visibleApps = PLATFORM_APPS.filter(
    (app) =>
      (app.key === currentAppKey || app.hasAccess(roles)) &&
      (app.key === currentAppKey || !!links?.[app.key]),
  )
  const portalHref = links?.portal ?? PORTAL_FALLBACK_URL

  const entryClass =
    "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors " +
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400"

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-label="Apps und Portal"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="rounded-md p-2 text-slate-300 hover:bg-slate-800 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400"
      >
        {/* 3×3-Punkteraster */}
        <svg viewBox="0 0 20 20" width="20" height="20" fill="currentColor" aria-hidden="true">
          {[2, 10, 18].flatMap((y) =>
            [2, 10, 18].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.8" />),
          )}
        </svg>
      </button>

      {open && (
        <>
          {/* Backdrop (nur mobil sichtbar, Sheet-Modus) */}
          <div
            className="fixed inset-0 z-40 bg-black/50 sm:hidden"
            aria-hidden="true"
            onClick={() => setOpen(false)}
          />
          <div
            ref={panelRef}
            role="dialog"
            aria-label="App-Wechsler"
            onKeyDown={onPanelKeyDown}
            className="fixed inset-x-0 bottom-0 z-50 max-h-[80vh] overflow-y-auto rounded-t-2xl border border-[#24374d] bg-[#14202f] p-3 text-[#e6edf5] shadow-2xl sm:absolute sm:inset-x-auto sm:bottom-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 sm:rounded-xl"
          >
            {/* Portal — immer zuerst, der Heimweg */}
            <a href={portalHref} className={`${entryClass} hover:bg-[#1a2a3d]`}>
              <span style={{ color: PORTAL_ENTRY.accent }}>{PORTAL_ENTRY.icon}</span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold">{PORTAL_ENTRY.title}</span>
                <span className="block truncate text-xs text-[#8fa3b8]">
                  {PORTAL_ENTRY.subtitle}
                </span>
              </span>
            </a>

            <div className="my-2 border-t border-[#24374d]" aria-hidden="true" />

            {visibleApps.map((app) => {
              const isCurrent = app.key === currentAppKey
              const inner = (
                <>
                  <span style={{ color: app.accent }} className={isCurrent ? "opacity-50" : ""}>
                    {app.icon}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold">
                      {app.title}
                      {isCurrent && (
                        <span className="ml-2 text-xs font-normal text-[#5c7089]">
                          Sie sind hier
                        </span>
                      )}
                    </span>
                    <span className="block truncate text-xs text-[#8fa3b8]">{app.subtitle}</span>
                  </span>
                </>
              )
              return isCurrent ? (
                <div
                  key={app.key}
                  aria-current="page"
                  className={`${entryClass} cursor-default text-[#8fa3b8]`}
                >
                  {inner}
                </div>
              ) : (
                <a key={app.key} href={links![app.key]} className={`${entryClass} hover:bg-[#1a2a3d]`}>
                  {inner}
                </a>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
